import type React from 'react';

/**
 * Container width tiers for page layouts
 */
export type ContainerTier = 'narrow' | 'medium' | 'wide' | 'full';

/**
 * Page Container Styles
 *
 * Left-aligned containers with consistent padding.
 * Content stays stable when drawer expands/collapses.
 *
 * Width Tiers:
 * - narrow (500px): Auth forms, simple wizards, password reset
 * - medium (800px): Profile, settings, simple forms
 * - wide (1200px): Admin config, help pages, moderate tables
 * - full (no limit): Dashboards, reports, large tables
 *
 * Usage:
 * ```tsx
 * import { getContainerStyle } from '@/styles/containers';
 * import { useMediaQuery } from '@/hooks/useMediaQuery';
 *
 * const { isMobile } = useMediaQuery();
 * const containerStyle = getContainerStyle('wide', isMobile);
 *
 * <div style={containerStyle}>...</div>
 * ```
 */
export const containers: Record<ContainerTier, React.CSSProperties> = {
  /**
   * Narrow - Auth forms, simple wizards, password reset
   * Max: 500px
   */
  narrow: {
    maxWidth: '500px',
    padding: '0 2rem',
    width: '100%',
  },

  /**
   * Medium - Profile, settings, simple forms
   * Max: 800px
   */
  medium: {
    maxWidth: '800px',
    padding: '0 2rem',
    width: '100%',
  },

  /**
   * Wide - Admin config, help pages, moderate tables
   * Max: 1200px
   */
  wide: {
    maxWidth: '1200px',
    padding: '0 2rem',
    width: '100%',
  },

  /**
   * Full - Dashboards, reports, large tables
   * No max-width, uses all available space
   */
  full: {
    width: '100%',
    padding: '0 2rem',
  },
};

/**
 * Mobile Container Overrides
 *
 * Same width tiers but with tighter padding (1rem instead of 2rem)
 * to maximize screen real estate on mobile devices.
 */
export const containersMobile: Record<ContainerTier, React.CSSProperties> = {
  narrow: { ...containers.narrow, padding: '0 1rem' },
  medium: { ...containers.medium, padding: '0 1rem' },
  wide: { ...containers.wide, padding: '0 1rem' },
  full: { ...containers.full, padding: '0 1rem' },
};

/**
 * Helper function to get appropriate container style based on screen size
 *
 * @param tier - The width tier (narrow, medium, wide, full)
 * @param isMobile - Whether the screen is mobile-sized
 * @returns The appropriate container style
 */
export const getContainerStyle = (tier: ContainerTier, isMobile: boolean): React.CSSProperties => {
  return isMobile ? containersMobile[tier] : containers[tier];
};
